
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import * as TXP from 'app/calcs/Accountant/Transactions/predicates'
import { FandfPensionCalculations } from 'app/calcs/customers/fandf'
import { IOPensionCalculations } from 'app/calcs/customers/IO'
import { makeIOComputer } from 'app/calcs/customers/IO/Factory';
import { Local145PensionCalculations } from 'app/calcs/customers/local145';
import { Local697PensionCalculations } from 'app/calcs/customers/local697'
import { Local697PlanPPensionCalculations } from 'app/calcs/customers/local697PlanP'
import { Local701PensionCalculations } from 'app/calcs/customers/local701'
import { Local705PensionCalculations } from 'app/calcs/customers/local705'
import { INcpersPensionInputs, NCPERSPensionCalculations } from 'app/calcs/customers/ncpers/current'
import { INcpersPriorPensionInputs, NCPERSPriorPensionCalculations } from 'app/calcs/customers/ncpers/prior'
import { NEBFPensionCalculations } from 'app/calcs/customers/NEBF'
import { makeNEBFComputer } from 'app/calcs/customers/NEBF/Factory';
import * as Expenses from 'app/calcs/expenses'
import { ISpousalPensionInputs, SpousalPensionCalculations } from 'app/calcs/pensions/spousal'
import { calculateRetirementScore, IScoreTransactionsOutput, scoreTransactions } from 'app/calcs/Planners/Fidelity'
import { RetirementSavingsCalculations } from 'app/calcs/retirementSavings'
import { SSABenefitsCalculations } from 'app/calcs/SSA'
import { IRetirementPerson, isValidPerson, Profile, retirementDate } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import * as DateCalcs from 'app/utils/DateCalcs'
import { configName } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'

let name701 = 'Local 701'
if (configName === 'XXX') { name701 = 'Local XYZ' }
if (configName === '74') { name701 = 'Local 74' }
if (configName === '401') { name701 = 'Local 401' }

class IncomeAndExpenseCalculations implements TX.ITransactionGenerator {
  protected expenseCalcs: Expenses.ExpenseCalculations
  protected spousalCalcs: SpousalPensionCalculations
  protected fandfCalcs: FandfPensionCalculations
  protected local697Calcs: Local697PensionCalculations
  protected local697PlanPCalcs: Local697PlanPPensionCalculations
  protected local701Calcs: Local701PensionCalculations
  protected IOCalcs: IOPensionCalculations
  protected NEBFCalcs: NEBFPensionCalculations
  protected local145Calcs: Local145PensionCalculations
  protected local705Calcs: Local705PensionCalculations
  protected ncpersCalcs: NCPERSPensionCalculations
  protected ncpersPriorCalcs: NCPERSPriorPensionCalculations
  protected retirementSavingsCalcs: RetirementSavingsCalculations
  protected ssaBenefitsCalcs: SSABenefitsCalculations

  constructor(protected profile: Profile) {
    this.expenseCalcs = new Expenses.ExpenseCalculations(this.profile)
    this.spousalCalcs = new SpousalPensionCalculations(this.profile)
    this.fandfCalcs = new FandfPensionCalculations(this.profile)
    this.local697Calcs = new Local697PensionCalculations(this.profile)
    this.local697PlanPCalcs = new Local697PlanPPensionCalculations(this.profile)
    this.local701Calcs = new Local701PensionCalculations(this.profile, name701)
    this.IOCalcs = makeIOComputer(this.profile)
    this.NEBFCalcs = makeNEBFComputer(this.profile)
    this.local705Calcs = new Local705PensionCalculations(this.profile)
    this.local145Calcs = new Local145PensionCalculations(this.profile)
    this.ncpersCalcs = new NCPERSPensionCalculations(this.profile)
    this.ncpersPriorCalcs = new NCPERSPriorPensionCalculations(this.profile)
    this.retirementSavingsCalcs = new RetirementSavingsCalculations(profile)
    this.ssaBenefitsCalcs = new SSABenefitsCalculations(profile)
  }

  getRetirementDateTransactions(): Promise<TX.ITransactionItem[]> {
    const r: TX.ITransactionItem[] = []

    const personsTransactions = (person: IRetirementPerson): TX.ITransactionItem[] => [{
      when: retirementDate(person),
      section: 'Retirement Savings',
      source: 'retirement',
      who: person.firstName,
      what: {
        amount: 0
      },
      description: person.firstName + ' retires at age <a>' + utils.toNiceAge(moment.duration(person.selectedRetirementAge, 'years')) + '</a>'
    }]

    const member = this.profile.getHeadOfHousehold()
    if (member) {
      r.push(...personsTransactions(member))
    }

    const spouse = this.profile.getSpouse()
    if (spouse && isValidPerson(spouse)) {
      r.push(...personsTransactions(spouse))
    }

    return Promise.resolve(_.uniqBy(r, 'description'))
  }

  /**
   * Gets the [noteworthy] transactions over time.
   */
  getTimeLineTransactions(): Promise<TX.ITransactionItem[]> {

    // define the work we have to do
    const work = [
      this.spousalCalcs.getTimeLineTransactions(),
      this.ncpersCalcs.getTimeLineTransactions(),
      this.fandfCalcs.getTimeLineTransactions(),
      this.local697Calcs.getTimeLineTransactions(),
      // removed: RA-283/FF-117
      //this.local697PlanPCalcs.getTimeLineTransactions(),
      this.local701Calcs.getTimeLineTransactions(),
      this.IOCalcs.getTimeLineTransactions(),
      this.NEBFCalcs.getTimeLineTransactions(),
      this.local705Calcs.getTimeLineTransactions(),
      this.local145Calcs.getTimeLineTransactions(),
      this.ncpersPriorCalcs.getTimeLineTransactions(),
      this.expenseCalcs.getTimeLineTransactions(),
      this.retirementSavingsCalcs.getTimeLineTransactions(),
      // removed per F&andF request
      //this.getRetirementDateTransactions(),
      this.ssaBenefitsCalcs.getTimeLineTransactions()
    ]

    return Promise.all(work)
      .then(_.flatten)
      .then(TXP.sortByWhenAndAmountDesc)
      .then((r1) => {
        if (0 !== r1.length) {
          const x = r1[0]
          r1.push(...this.expenseCalcs.getInitialExpenseTransaction(x.when))
        }
        return r1
      })
  }

  /**
   * Gets all the transactions for a given moment.
   *
   * @param when
   * @param tolerance
   *
   * @returns An array of transaction items, ordered by moment (see tolerance)
   *          and then by amount (descending)
   */
  getTransactions(when: moment.Moment, tolerance: TX.TransactionTolerance = 'month'): Promise<TX.ITransactionItem[]> {

    when = when.clone()

    // define the work we have to do
    const work = [
      this.spousalCalcs.getTransactions(when),
      this.fandfCalcs.getTransactions(when),
      this.local697Calcs.getTransactions(when),
      this.local697PlanPCalcs.getTransactions(when),
      this.local701Calcs.getTransactions(when),
      this.IOCalcs.getTransactions(when),
      this.NEBFCalcs.getTransactions(when),
      this.local705Calcs.getTransactions(when),
      this.local145Calcs.getTransactions(when),
      this.ncpersCalcs.getTransactions(when),
      this.ncpersPriorCalcs.getTransactions(when),
      this.expenseCalcs.getTransactions(when),
      this.retirementSavingsCalcs.getTransactions(when),
      this.ssaBenefitsCalcs.getTransactions(when)
    ]

    return Promise.all(work)
      .then(_.flatten)
      .then(TXP.sortByWhenAndAmountDesc)
  }
}

export {
  IncomeAndExpenseCalculations
}



// WEBPACK FOOTER //
// ./src/app/calcs/Accountant/Transactions/index.ts